import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError } from '#src/shared/error';

import { isEligibleForTrafficPercentage } from '#src/app/shared/utils/featureRollout';

import type { EndorsementsLayoutPageData } from '../types';

import { computePetitionStrengthScore } from './computePetitionStrengthScore';
import { EndorsementsLayoutPageData as EndorsementsLayoutPageDataDocument } from './endorsementsLayoutPageData.graphql';
import type {
	EndorsementsLayoutPageDataQuery,
	EndorsementsLayoutPageDataQueryVariables,
} from './endorsementsLayoutPageData.graphql';

export type EndorsementsLayoutPageDataParams = Readonly<{
	slug: string;
}>;

function mapPetitionForPageState(petition: NonNullable<EndorsementsLayoutPageDataQuery['petition']>) {
	const tagsConnection = petition.tagsConnection ?? { nodes: [] };
	return {
		id: petition.id,
		slug: petition.slug,
		geoReach: petition.geoReach,
		relevantLocationLocalizedName: petition.relevantLocationLocalizedName,
		user: petition.user,
		createdAt: petition.createdAt,
		signatureCount: petition.signatureState.signatureCount,
		tags: tagsConnection.nodes,
		decisionMakers: petition.decisionMakersConnection.edges.map((edge) => ({
			...edge.node,
			targetPetitionAssociationData: edge.associationData,
		})),
		score: computePetitionStrengthScore(petition),
	};
}

export async function getEndorsementsLayoutPageData(
	utilityContext: UtilityContext,
	{ slug }: EndorsementsLayoutPageDataParams,
): Promise<EndorsementsLayoutPageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const { data } = await fetch<EndorsementsLayoutPageDataQuery, EndorsementsLayoutPageDataQueryVariables>({
		query: EndorsementsLayoutPageDataDocument,
		variables: { slug },
		rejectOnError: true,
	});

	if (!data.petition) {
		throw new NotFoundError();
	}

	const starterUserId = data.petition.user.id;

	return {
		petition: mapPetitionForPageState(data.petition),
		viewer: data.viewer,
		iOSBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppIosMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		androidBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppAndroidMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		desktopBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppCombinedDesktopDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
	};
}
